import React, { useState, useEffect, memo } from 'react';
import { Copy, Check, X } from 'lucide-react';
import { Button } from '../../../../components/ui/Button';
import { FilterableSelect } from '../../../../components/ui/FilterableSelect';
import { generateRandomToken } from '../../../../utils/functions';
import TextField from './helpers/TextField';

const subtitleClass = 'text-sm font-bold text-ninja-dark dark:text-white mb-3';

const Prestashop = ({ ecommerce, warehouses, onSave, loading, saving = false, isEdit }) => {
  const [formData, setFormData] = useState(ecommerce);
  const [deliveryInput, setDeliveryInput] = useState("");
  const [copiedTooltip, setCopiedTooltip] = useState("");

  useEffect(() => {
    if (!formData.configuration.authentication.token) {
      const newToken = generateRandomToken();
      setFormData((prev) => ({
        ...prev,
        configuration: {
          ...prev.configuration,
          authentication: {
            ...prev.configuration.authentication,
            token: newToken,
          },
        },
      }));
    }
    // Only on mount, as in the legacy.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      configuration: {
        ...prev.configuration,
        authentication: {
          ...prev.configuration.authentication,
          [field]: value,
        },
      },
    }));
  };

  const handleWarehouseChange = (value) => {
    setFormData({ ...formData, warehouse: value });
  };

  const addDeliveryMethod = () => {
    if (!deliveryInput) return;
    const currentMethods =
      formData.configuration.options.enabled_delivery_methods || [];
    if (!currentMethods.includes(deliveryInput)) {
      setFormData((prev) => ({
        ...prev,
        configuration: {
          ...prev.configuration,
          options: {
            ...prev.configuration.options,
            enabled_delivery_methods: [...currentMethods, deliveryInput],
          },
        },
      }));
    }
    setDeliveryInput("");
  };

  const removeDeliveryMethod = (method) => {
    const currentMethods =
      formData.configuration.options.enabled_delivery_methods.filter(
        (m) => m !== method,
      );
    setFormData((prev) => ({
      ...prev,
      configuration: {
        ...prev.configuration,
        options: {
          ...prev.configuration.options,
          enabled_delivery_methods: currentMethods,
        },
      },
    }));
  };

  const handleCopy = (text, fieldName) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopiedTooltip(fieldName);
      setTimeout(() => setCopiedTooltip(""), 2000);
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const deliveryMethods = formData.configuration.options.enabled_delivery_methods || [];

  return (
    <form data-testid="ecommerce-form" data-platform="prestashop">
      <h3 className="text-lg font-bold text-ninja-dark dark:text-white mb-5">Configuración PrestaShop</h3>

      <div className="bg-blue-50 dark:bg-blue-900/20 text-info p-3 rounded-xl border border-blue-200 dark:border-blue-900 text-sm mb-5">
        Configura los datos de tu tienda y copia el Token de seguridad y la URL
        del Webhook para pegarlos en tu módulo de PrestaShop.
      </div>

      <div className="mb-5">
        <FilterableSelect
          variant="form"
          testId="ecommerce-warehouse"
          label="Bodega Asociada"
          value={formData.warehouse || ""}
          onChange={handleWarehouseChange}
          options={warehouses.map((w) => ({ value: w.id, label: w.name }))}
          placeholder="Seleccionar..."
        />
      </div>

      <div className="space-y-4 mb-5">
        <p className={subtitleClass}>Credenciales API</p>
        <TextField
          label="Nombre de la Tienda"
          value={formData.configuration.authentication.shop_name || ""}
          onChange={(e) => handleChange("shop_name", e.target.value)}
        />
        <TextField
          label="URL de la Tienda"
          placeholder="https://mitienda.com"
          value={formData.configuration.authentication.shop_url || ""}
          onChange={(e) => handleChange("shop_url", e.target.value)}
        />
      </div>

      <div className="h-px bg-border my-6" />

      <p className={subtitleClass}>Configuración del Módulo (Copiar a PrestaShop)</p>

      <div className="space-y-4 mb-5">
        <TextField
          label="Token de Seguridad"
          value={formData.configuration.authentication.token || ""}
          disabled
          readOnly
          helperText="Copia este token y pégalo en la configuración del módulo en PrestaShop"
          endAdornment={
            <button
              type="button"
              onClick={() => handleCopy(formData.configuration.authentication.token, "token")}
              title={copiedTooltip === "token" ? "¡Copiado!" : "Copiar Token"}
              aria-label="Copiar Token de Seguridad"
              className="p-1.5 rounded-lg text-text-secondary hover:text-ninja-green transition-colors"
            >
              {copiedTooltip === "token" ? <Check size={16} /> : <Copy size={16} />}
            </button>
          }
        />

        <TextField
          label="Webhook URL (Crear Orden)"
          value={
            formData.configuration.create_order?.url ||
            "Guarda la integración para generar la URL"
          }
          disabled
          readOnly
          endAdornment={
            <button
              type="button"
              onClick={() => handleCopy(formData.configuration.create_order?.url, "webhook")}
              disabled={!formData.configuration.create_order?.url}
              title={copiedTooltip === "webhook" ? "¡Copiado!" : "Copiar Webhook"}
              aria-label="Copiar Webhook URL"
              className="p-1.5 rounded-lg text-text-secondary hover:text-ninja-green transition-colors disabled:opacity-40"
            >
              {copiedTooltip === "webhook" ? <Check size={16} /> : <Copy size={16} />}
            </button>
          }
        />
      </div>

      <div data-testid="ecommerce-delivery-methods" className="mt-8 mb-5 border border-border rounded-xl p-4">
        <span className="block text-sm font-semibold text-ninja-dark dark:text-white mb-2">
          Métodos de Envío Habilitados
        </span>
        <div className="flex items-center gap-2 mb-3">
          <TextField
            className="flex-1"
            aria-label="Método de envío"
            placeholder="Ej: Starken, Chilexpress..."
            value={deliveryInput}
            onChange={(e) => setDeliveryInput(e.target.value)}
          />
          <Button type="button" variant="primary" onClick={addDeliveryMethod} disabled={!deliveryInput}>
            Agregar
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {deliveryMethods.length > 0 ? (
            deliveryMethods.map((m) => (
              <span
                key={m}
                data-testid="ecommerce-delivery-method"
                className="inline-flex items-center gap-1 px-3 py-1 rounded-full border border-ninja-green text-ninja-green text-sm"
              >
                {m}
                <button type="button" aria-label={`Quitar ${m}`} onClick={() => removeDeliveryMethod(m)} className="hover:text-danger">
                  <X size={14} />
                </button>
              </span>
            ))
          ) : (
            <span className="text-sm text-text-muted">No hay métodos configurados</span>
          )}
        </div>
      </div>

      <Button
        type="submit"
        variant="primary"
        size="lg"
        fullWidth
        disabled={loading}
        loading={saving}
        loadingLabel={isEdit ? "Actualizando..." : "Guardando..."}
        onClick={handleSubmit}
      >
        {isEdit ? "Actualizar Integración" : "Guardar Integración"}
      </Button>
    </form>
  );
};

export default memo(Prestashop);
